<template>
  <el-dialog :title="$t('mainCategoryProduct.add')" :visible.sync="dialogVisible" width="80%" @close="handleClose">
    <!-- 2026-07-03 改造：由手动输入商品编号改为弹窗选择商品 -->
    <div class="category-info" v-if="currentCategory">
      <span class="label">当前专区：</span>
      <span class="value">{{ currentCategory.categoryName }}</span>
      <span class="label" style="margin-left: 20px;">匹配规则：</span>
      <span class="value">{{ matchTypeText }} {{ matchValueText }}</span>
    </div>

    <el-form :inline="true" class="demo-form-inline" style="margin-top: 15px;">
      <el-form-item label="商品名称">
        <el-input v-model="productQuery.productNameLike" placeholder="请输入商品名称" clearable />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" size="small" @click="_queryProducts(1)">查询</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="productList" style="width: 100%" v-loading="loading"
      @selection-change="handleSelectionChange"
      ref="productTable"
      row-key="productId"
    >
      <el-table-column type="selection" width="55" :selectable="isSelectable"></el-table-column>
      <el-table-column prop="productId" label="商品编号" align="center"></el-table-column>
      <el-table-column prop="prodName" label="商品名称" align="center"></el-table-column>
      <el-table-column prop="categoryName" label="商品分类" align="center"></el-table-column>
      <el-table-column prop="shopName" label="所属店铺" align="center"></el-table-column>
      <el-table-column prop="stateName" label="状态" align="center"></el-table-column>
      <el-table-column label="是否符合规则" align="center">
        <template slot-scope="scope">
          <el-tag v-if="isSelectable(scope.row)" type="success" size="mini">符合</el-tag>
          <el-tag v-else type="danger" size="mini">不符合</el-tag>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      @current-change="handleCurrentChange"
      :current-page="productQuery.page"
      :page-size="productQuery.row"
      layout="total, prev, pager, next"
      :total="productTotal"
      style="margin-top: 15px;"
    >
    </el-pagination>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
      <el-button type="primary" @click="submitForm" :disabled="selectedProducts.length === 0">
        保存 (已选{{ selectedProducts.length }}个)
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { saveMainCategoryProduct } from '@/api/admin/mainCategoryProduct/mainCategoryProductApi'
import { queryProduct } from '@/api/goods/productApi'

/**
 * 新增专区商品弹窗（商品选择器）
 * 2026-07-03 改造：
 * 1. 由手动输入商品编号改为弹窗选择商品
 * 2. 根据当前专区的匹配规则高亮/禁用不符合规则的商品
 * 3. 支持批量选择多个商品
 */
export default {
  name: 'AddMainCategoryProduct',
  data() {
    return {
      dialogVisible: false,
      loading: false,
      // 当前选中的专区信息
      currentCategory: null,
      // 商品查询条件
      productQuery: {
        page: 1,
        row: 10,
        state: '2002', // 只查已上架商品
        productNameLike: ''
      },
      productList: [],
      productTotal: 0,
      selectedProducts: []
    }
  },
  computed: {
    /**
     * 匹配类型中文展示
     */
    matchTypeText() {
      const map = {
        'ALL': '全部商品',
        'CATEGORY': '商品分类',
        'LABEL': '商品标签',
        'SHOP': '店铺'
      }
      return (this.currentCategory && this.currentCategory.matchType) ? map[this.currentCategory.matchType] : '全部商品'
    },
    /**
     * 匹配值中文展示
     */
    matchValueText() {
      if (!this.currentCategory || this.currentCategory.matchType === 'ALL') {
        return ''
      }
      return this.currentCategory.matchValue ? `(${this.currentCategory.matchValue})` : ''
    }
  },
  methods: {
    /**
     * 打开弹窗
     * @param {Object} category 当前专区信息，包含 mainCategoryId、categoryName、matchType、matchValue
     */
    open(category) {
      this.currentCategory = category
      this.dialogVisible = true
      this.selectedProducts = []
      this.productQuery.page = 1
      this.productQuery.productNameLike = ''
      this._queryProducts(1)
    },
    /**
     * 查询已上架商品列表
     */
    async _queryProducts(page) {
      this.loading = true
      this.productQuery.page = page
      try {
        // 根据专区匹配规则预过滤查询条件
        const params = { ...this.productQuery }
        if (this.currentCategory) {
          if (this.currentCategory.matchType === 'CATEGORY' && this.currentCategory.matchValue) {
            // 分类规则：取第一个分类ID作为查询条件（减少数据量）
            params.categoryId = this.currentCategory.matchValue.split(',')[0].trim()
          } else if (this.currentCategory.matchType === 'SHOP' && this.currentCategory.matchValue) {
            // 店铺规则：取第一个店铺ID作为查询条件
            params.shopId = this.currentCategory.matchValue.split(',')[0].trim()
          }
        }
        const res = await queryProduct(params)
        this.productList = res.data || []
        this.productTotal = res.total || 0
      } catch (error) {
        console.error('查询商品列表失败:', error)
        this.$message.error('查询商品列表失败')
      } finally {
        this.loading = false
      }
    },
    /**
     * 判断商品是否符合当前专区的匹配规则
     * 用于禁用不符合规则的商品复选框
     */
    isSelectable(row) {
      if (!this.currentCategory) {
        return false
      }
      const matchType = this.currentCategory.matchType
      const matchValue = this.currentCategory.matchValue || ''

      // ALL 类型不限制
      if (!matchType || matchType === 'ALL' || matchValue === '') {
        return true
      }

      const values = matchValue.split(',').map(v => v.trim()).filter(v => v)

      if (matchType === 'CATEGORY') {
        return values.includes(row.categoryId)
      }

      if (matchType === 'SHOP') {
        return values.includes(row.shopId)
      }

      if (matchType === 'LABEL') {
        // 商品标签需要查询后判断，这里先默认可选，保存时后端会再次校验
        // 如果后端返回不符合规则的错误，前端会提示
        return true
      }

      return true
    },
    /**
     * 表格选择变更
     */
    handleSelectionChange(selection) {
      // 只保留符合规则的商品
      this.selectedProducts = selection.filter(item => this.isSelectable(item))
    },
    /**
     * 分页切换
     */
    handleCurrentChange(val) {
      this._queryProducts(val)
    },
    /**
     * 保存专区商品
     */
    async submitForm() {
      if (this.selectedProducts.length === 0) {
        this.$message.warning('请选择商品')
        return
      }

      try {
        // 逐个保存选中的商品
        for (const product of this.selectedProducts) {
          const form = {
            mainCategoryId: this.currentCategory.mainCategoryId,
            productId: product.productId,
            startTime: this._formatDate(new Date()),
            endTime: this._formatDate(new Date(new Date().getFullYear() + 1, 11, 31, 23, 59, 59)),
            seq: '0'
          }
          await saveMainCategoryProduct(form)
        }
        this.$message.success(`成功添加 ${this.selectedProducts.length} 个商品到专区`)
        this.dialogVisible = false
        this.$emit('refresh')
      } catch (error) {
        console.error('添加失败:', error)
        this.$message.error(error.message || '添加失败')
      }
    },
    /**
     * 格式化日期为 yyyy-MM-dd HH:mm:ss
     */
    _formatDate(date) {
      const pad = (n) => n < 10 ? '0' + n : n
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    handleClose() {
      this.productList = []
      this.selectedProducts = []
      this.currentCategory = null
    }
  }
}
</script>

<style scoped>
.category-info {
  padding: 10px 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 10px;
}

.category-info .label {
  color: #606266;
  font-weight: bold;
}

.category-info .value {
  color: #303133;
}
</style>
